import React, { useRef, useMemo } from 'react';
import JoditEditor from 'jodit-react';
import 'jodit/es2021/jodit.min.css';

interface HtmlEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: string;
  showPlaceholders?: boolean;
}

const HtmlEditor: React.FC<HtmlEditorProps> = ({ 
  value, 
  onChange, 
  placeholder = 'Enter content here...',
  minHeight = '300px',
  showPlaceholders = true
}) => {
  const editor = useRef(null);

  // Jodit configuration
  const config = useMemo(() => ({
    readonly: false,
    placeholder: placeholder,
    minHeight: parseInt(minHeight) || 300,
    toolbar: true,
    toolbarSticky: false,
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    buttons: [
      'bold',
      'italic',
      'underline',
      '|',
      'ul',
      'ol',
      '|',
      'font',
      'fontsize',
      'brush',
      '|',
      'paragraph',
      'align',
      '|',
      'link',
      'image',
      '|',
      'undo',
      'redo',
      '|',
      'hr',
      'table',
      '|',
      'source'
    ],
    uploader: {
      insertImageAsBase64URI: true
    },
    enter: 'p' as 'p' | 'div' | 'br',
    defaultMode: 1,
    style: {
      font: '14px Arial, sans-serif',
      color: '#333'
    }
  }), [placeholder, minHeight]);

  return (
    <div className="wysiwyg-editor-wrapper">
      <JoditEditor
        ref={editor}
        value={value}
        config={config}
        onBlur={(newContent) => onChange(newContent)}
        onChange={(newContent) => {}} // Use onBlur for better performance
      />

      {/* Placeholders Help */}
      {showPlaceholders && (
        <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-navy-800">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            <strong>Available placeholders:</strong>{' '}
            <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">{'{{first_name}}'}</code>{' '}
            <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">{'{{last_name}}'}</code>{' '}
            <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">{'{{user_email}}'}</code>{' '}
            <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">{'{{code}}'}</code>{' '}
            <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">{'{{verification_link}}'}</code>{' '}
            <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">{'{{reset_link}}'}</code>{' '}
            <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">{'{{dashboard_url}}'}</code>
          </p>
        </div>
      )}

      <style>{`
        .wysiwyg-editor-wrapper .jodit-container {
          border-radius: 0.5rem;
          border: 1px solid #d1d5db;
        }
        
        .dark .wysiwyg-editor-wrapper .jodit-container {
          border-color: #4b5563;
        }
        
        .wysiwyg-editor-wrapper .jodit-toolbar-button {
          border-radius: 0.25rem;
        }
        
        .wysiwyg-editor-wrapper .jodit-workplace {
          min-height: ${minHeight};
          background: white;
        }
        
        .dark .wysiwyg-editor-wrapper .jodit-workplace {
          background: #1e293b;
          color: white;
        }
        
        .wysiwyg-editor-wrapper .jodit-status-bar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default HtmlEditor;
